import React, { useEffect, useState } from 'react';
import { db, auth } from '../services/firebase';
import { collection, query, where, orderBy, serverTimestamp, doc, getDoc, deleteDoc, onSnapshot, addDoc } from 'firebase/firestore';
import { Box, Typography, Card, CardContent, TextField, Button, List, ListItem, ListItemText, IconButton } from '@mui/material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css'; // Estilos do editor
import DOMPurify from 'dompurify'; // Para renderizar HTML de forma segura
import DeleteIcon from '@mui/icons-material/Delete';
import styles from '../styles/Dashboard.module.css';

const Comunicados = () => {
  const [comunicados, setComunicados] = useState([]);
  const [novoComunicado, setNovoComunicado] = useState({ titulo: '', conteudo: '' });
  const [condominioId, setCondominioId] = useState('');
  const [userRole, setUserRole] = useState('');

  useEffect(() => {
    // Busca o ID do condomínio e o papel do usuário logado
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        const userDocRef = doc(db, 'tb_usuarios', user.uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setCondominioId(userData.condominio_id);
          setUserRole(userData.papel); // **CORREÇÃO: Define o papel do usuário no estado**
        }
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!condominioId) return; // Só busca comunicados se já tivermos o ID do condomínio

    const q = query(collection(db, 'tb_comunicados'), where('condominio_id', '==', condominioId), orderBy('created_at', 'desc'));
    // Usamos onSnapshot para ouvir as mudanças em tempo real (melhor para UX)
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const comunicadosList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setComunicados(comunicadosList);
    }, (error) => {
      console.error("Erro ao buscar comunicados:", error);
    });

    return () => unsubscribe(); // Limpa o listener de tempo real ao desmontar
  }, [condominioId]); // Re-executa quando condominioId mudar

  const handleCreateComunicado = async () => {
    if (!novoComunicado.titulo || !novoComunicado.conteudo) {
      alert('Por favor, preencha o título e o conteúdo.');
      return;
    }
    if (!condominioId) {
      alert('Erro: ID do condomínio não encontrado. Não é possível criar o comunicado.');
      return;
    }
    try {
      await addDoc(collection(db, 'tb_comunicados'), {
        ...novoComunicado,
        // Adiciona a referência ao condomínio
        condominio_id: condominioId,
        created_at: serverTimestamp(),
      });
      // Não precisa mais adicionar localmente, o onSnapshot cuida disso.
      setNovoComunicado({ titulo: '', conteudo: '' });
    } catch (error) {
      alert('Erro ao criar comunicado: ' + error.message);
    }
  };

  const handleDeleteComunicado = async (id) => {
    if (!window.confirm("Tem certeza que deseja deletar este comunicado?")) {
      return;
    }
    try {
      const comunicadoDocRef = doc(db, 'tb_comunicados', id);
      await deleteDoc(comunicadoDocRef);

      // Não precisa mais remover localmente, o onSnapshot cuida disso.
      alert('Comunicado deletado com sucesso!');
    } catch (error) {
      alert('Erro ao deletar comunicado: ' + error.message);
    }
  };

  const isAdmin = userRole && ['sindico', 'sub_sindico', 'conselheiro'].includes(userRole);

  return (
    <Box>
      <Box component="main" sx={{ flexGrow: 1, p: 3, background: 'linear-gradient(45deg, #283593 30%, #1976d2 90%)', minHeight: '100vh' }}>
        <Typography variant="h4" gutterBottom sx={{ color: 'white' }}>Comunicados</Typography>
        
        {isAdmin && (
          <Card className={styles.card} sx={{ mb: 4 }}>
            <CardContent>
              <Typography variant="h5" className={styles.sectionTitle}>Criar Novo Comunicado</Typography>
              <TextField
                fullWidth
                label="Título"
                value={novoComunicado.titulo}
                onChange={(e) => setNovoComunicado({ ...novoComunicado, titulo: e.target.value })}
                sx={{ mb: 2 }}
                required
              />
              <Box sx={{ 
                '& .ql-editor': { minHeight: '180px' }, 
                '& .ql-container': { borderRadius: '0 0 4px 4px' },
                '& .ql-toolbar': { borderRadius: '4px 4px 0 0', backgroundColor: '#f5f5f5' },
                mb: 2 
              }}>
                <ReactQuill
                  theme="snow"
                  value={novoComunicado.conteudo}
                  onChange={(content) => setNovoComunicado({ ...novoComunicado, conteudo: content })}
                  style={{ backgroundColor: 'white', color: 'black' }}
                />
              </Box>
              <Button variant="contained" onClick={handleCreateComunicado}>Publicar</Button>
            </CardContent>
          </Card>
        )}

        <Card className={styles.card}>
          <CardContent>
            <Typography variant="h5" className={styles.sectionTitle}>Lista de Comunicados</Typography>
            <List>
              {comunicados.map((comunicado) => (
                <ListItem
                  key={comunicado.id}
                  divider
                  secondaryAction={
                    userRole && ['sindico', 'sub_sindico', 'conselheiro'].includes(userRole) && (
                      <IconButton edge="end" aria-label="delete" onClick={() => handleDeleteComunicado(comunicado.id)}>
                        <DeleteIcon />
                      </IconButton>
                    )
                  }
                >
                  <ListItemText
                    primary={comunicado.titulo}
                    primaryTypographyProps={{ fontWeight: 'bold', color: 'primary.dark' }}
                    secondary={
                      <Typography
                        component="div"
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(comunicado.conteudo) }}
                      />
                    }
                  />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default Comunicados;